/**
 * Skeleton Loading Components - 2025 Best Practices
 * Provides visual feedback during lazy loading
 */

// Base skeleton with animation
export function Skeleton({ className = '', style = {} }) {
  return (
    <div
      className={`animate-pulse ${className}`}
      style={{
        background: 'linear-gradient(90deg, rgba(26,43,27,0.06) 0%, rgba(26,43,27,0.1) 50%, rgba(26,43,27,0.06) 100%)',
        backgroundSize: '200% 100%',
        animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
        borderRadius: '4px',
        ...style,
      }}
    />
  );
}

// Page-level skeleton for route transitions
export function PageSkeleton() {
  return (
    <div style={{ background: '#F5F5F5', minHeight: '100vh' }}>
      {/* Hero skeleton */}
      <div style={{ height: '70vh', background: 'rgba(26,43,27,0.08)' }}>
        <div className="max-w-5xl mx-auto px-6 pt-32">
          <Skeleton style={{ width: '120px', height: '12px', marginBottom: '16px' }} />
          <Skeleton style={{ width: '60%', height: '48px', marginBottom: '12px' }} />
          <Skeleton style={{ width: '40%', height: '48px', marginBottom: '24px' }} />
          <Skeleton style={{ width: '300px', height: '16px' }} />
        </div>
      </div>
      
      {/* Content skeleton */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i}>
              <Skeleton style={{ width: '100%', height: '200px', marginBottom: '12px' }} />
              <Skeleton style={{ width: '80%', height: '20px', marginBottom: '8px' }} />
              <Skeleton style={{ width: '60%', height: '14px' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Card skeleton for project/blog grids
export function CardSkeleton({ count = 3 }) {
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white p-4">
          <Skeleton style={{ width: '100%', height: '180px', marginBottom: '16px' }} />
          <Skeleton style={{ width: '40%', height: '10px', marginBottom: '8px' }} />
          <Skeleton style={{ width: '90%', height: '18px', marginBottom: '8px' }} />
          <Skeleton style={{ width: '70%', height: '14px' }} />
        </div>
      ))}
    </div>
  );
}

// Service page skeleton
export function ServiceSkeleton() {
  return (
    <div style={{ background: '#F5F5F5', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ height: '50vh', background: 'rgba(26,43,27,0.9)' }} className="flex items-center justify-center">
        <div className="text-center px-6">
          <Skeleton style={{ width: '100px', height: '10px', margin: '0 auto 16px', background: 'rgba(255,255,255,0.1)' }} />
          <Skeleton style={{ width: '400px', height: '40px', margin: '0 auto 12px', background: 'rgba(255,255,255,0.15)' }} />
          <Skeleton style={{ width: '300px', height: '16px', margin: '0 auto', background: 'rgba(255,255,255,0.08)' }} />
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Skeleton style={{ width: '200px', height: '12px', marginBottom: '24px' }} />
        <Skeleton style={{ width: '100%', height: '16px', marginBottom: '8px' }} />
        <Skeleton style={{ width: '95%', height: '16px', marginBottom: '8px' }} />
        <Skeleton style={{ width: '80%', height: '16px', marginBottom: '32px' }} />
        
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <Skeleton style={{ width: '100%', height: '250px' }} />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} style={{ width: '100%', height: '50px' }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Projects page skeleton
export function ProjectsSkeleton() {
  return (
    <div style={{ background: '#F5F5F5', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ height: '35vh', background: 'rgba(26,43,27,0.9)' }} className="flex items-center justify-center">
        <Skeleton style={{ width: '250px', height: '40px', background: 'rgba(255,255,255,0.15)' }} />
      </div>
      
      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-3 mb-8">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} style={{ width: '100px', height: '36px' }} />
          ))}
        </div>
        
        {/* Grid */}
        <CardSkeleton count={6} />
      </div>
    </div>
  );
}

// Contact page skeleton
export function ContactSkeleton() {
  return (
    <div style={{ background: '#F5F5F5', minHeight: '100vh' }}>
      <div style={{ height: '40vh', background: 'rgba(26,43,27,0.9)' }} />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} style={{ width: '100%', height: '48px' }} />
            ))}
            <Skeleton style={{ width: '100%', height: '120px' }} />
            <Skeleton style={{ width: '150px', height: '48px' }} />
          </div>
          <div>
            <Skeleton style={{ width: '100%', height: '300px' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Add CSS animation to global styles
export const skeletonStyles = `
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
`;
